/**
 * Broker Authentication Repository
 * Handles all authentication-related API calls
 */

import axios, { AxiosInstance } from 'axios'
import API_CONFIG from '../config/api.config'

interface LoginRequest {
  username: string
  password: string
}

interface LoginResponse {
  id_token: string
}

interface JWTPayload {
  sub: string
  auth: string
  iat: number
  exp: number
  pid: number // Partner ID
}

export interface UserAccount {
  id: number
  login: string
  firstName: string
  lastName: string
  email: string
  imageUrl: string
  activated: boolean
  langKey: string
  authorities: string[]
  partnerStaffRole?: string
}

class AuthRepository {
  private api: AxiosInstance

  constructor() {
    this.api = axios.create({
      timeout: API_CONFIG.TIMEOUT,
      headers: {
        'Content-Type': 'application/json'
      }
    })

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      response => response,
      error => {
        // Handle common auth errors
        if (error.response) {
          switch (error.response.status) {
            case 401:
              // Unauthorized - clear token and redirect to login
              this.clearAuthData()
              break
            case 403:
              // Forbidden
              console.error('Access forbidden:', error.response.data)
              break
            case 500:
              console.error('Server error:', error.response.data)
              break
          }
        }
        return Promise.reject(error)
      }
    )
  }

  /**
   * Login with username and password
   */
  async login(username: string, password: string): Promise<{ token: string; partnerId: number; user: any }> {
    try {
      console.log('Making login request to:', API_CONFIG.AUTH.LOGIN)
      console.log('With credentials:', { username, password: '***' })

      const response = await this.api.post<LoginResponse>(
        API_CONFIG.AUTH.LOGIN,
        { username, password } as LoginRequest
      )

      console.log('Login API response:', response.data)

      const token = response.data.id_token

      if (!token) {
        throw new Error('No token received from server')
      }

      // Parse JWT to extract user info and PID
      const payload = this.parseJWT(token)
      console.log('Parsed JWT payload:', payload)

      // Store auth data
      this.storeAuthData(token, payload)

      return {
        token,
        partnerId: payload.pid,
        user: {
          username: payload.sub,
          roles: payload.auth.split(','),
          partnerId: payload.pid
        }
      }
    } catch (error: any) {
      console.error('Login failed:', error)
      console.error('Error response:', error.response?.data)
      throw error
    }
  }

  /**
   * Parse JWT token to extract payload
   */
  parseJWT(token: string): JWTPayload {
    try {
      const base64Payload = token.split('.')[1]
      // Decode base64 (browser-compatible)
      const base64 = base64Payload.replace(/-/g, '+').replace(/_/g, '/')
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      )
      return JSON.parse(jsonPayload)
    } catch (error) {
      console.error('Failed to parse JWT:', error)
      throw new Error('Invalid token format')
    }
  }

  /**
   * Store authentication data in localStorage
   */
  private storeAuthData(token: string, payload: JWTPayload): void {
    localStorage.setItem('broker_token', token)
    localStorage.setItem('broker_pid', payload.pid.toString())
    localStorage.setItem('broker_user', payload.sub)
    localStorage.setItem('broker_token_exp', payload.exp.toString())
  }

  /**
   * Clear authentication data from localStorage
   */
  clearAuthData(): void {
    localStorage.removeItem('broker_token')
    localStorage.removeItem('broker_pid')
    localStorage.removeItem('broker_user')
    localStorage.removeItem('broker_token_exp')
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = this.getToken()
    if (!token) return false

    // Check token expiration
    const expiration = localStorage.getItem('broker_token_exp')
    if (expiration) {
      const expTime = parseInt(expiration) * 1000 // Convert to milliseconds
      if (Date.now() > expTime) {
        this.clearAuthData()
        return false
      }
    }

    return true
  }

  /**
   * Get stored token
   */
  getToken(): string | null {
    return localStorage.getItem('broker_token')
  }

  /**
   * Get stored partner ID
   */
  getPartnerId(): number | null {
    const pid = localStorage.getItem('broker_pid')
    return pid ? parseInt(pid) : null
  }

  /**
   * Get authorization header
   */
  getAuthHeader(): { Authorization: string } | {} {
    const token = this.getToken()
    return token ? { Authorization: `Bearer ${token}` } : {}
  }

  /**
   * Get current user account information including profile picture
   */
  async getUser(): Promise<UserAccount> {
    try {
      const token = this.getToken()

      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await this.api.get<UserAccount>(
        `${API_CONFIG.BASE_URL}/api/account`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      )

      return response.data
    } catch (error: any) {
      console.error('Failed to get user account:', error)
      throw error
    }
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    // Could call logout endpoint here if needed
    this.clearAuthData()
  }
}

export default new AuthRepository()